import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  inject,
  OnDestroy,
  PLATFORM_ID,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, of, throwError } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  filter,
  tap,
  switchMap,
  catchError,
} from 'rxjs/operators';

import { ShowClassicContentService } from '@digital-platform/rs-classic/services';
import { CmsPageComponent } from '@digital-platform/rs-cms/feature-cms-page';
import {
  CmsContentService,
  CmsPage,
  // CmsPagesService,
  RsCmsServicesModule,
} from '@digital-platform/rs-cms/services';
import { MetaDataService } from '@digital-platform/rs-meta/services';
import { PrerenderService } from '@digital-platform/rs-shared/services';
import { NotFoundSplashComponent } from '@digital-platform/rs-shared/ui';

@UntilDestroy()
@Component({
  selector: 'digital-platform-fallback-page',
  standalone: true,
  imports: [CommonModule, RouterModule, RsCmsServicesModule],
  templateUrl: './fallback-page.component.html',
  styleUrls: ['./fallback-page.component.scss'],
})
export class FallbackPageComponent implements AfterViewInit, OnDestroy {
  public isCms = false;
  public is404 = false;

  @ViewChild('contentContainer', { static: true, read: ViewContainerRef })
  public contentContainer!: ViewContainerRef;

  @ViewChild('errorMessage', { static: true, read: TemplateRef })
  public errorMessage!: TemplateRef<FallbackPageComponent>;

  private cmsContentService = inject(CmsContentService);
  private route = inject(ActivatedRoute);
  private platformId = inject(PLATFORM_ID);
  private showClassicContentService = inject(ShowClassicContentService);
  private prerenderService = inject(PrerenderService);
  private metaDataService = inject(MetaDataService);

  // get the page path from the route data and fetch the content from CMS
  private pagePath$: Observable<string> = this.route.data.pipe(
    untilDestroyed(this),
    map((data) => data['pagePath']),
    distinctUntilChanged(),
    filter((path) => !!path),
    tap((path) => {
      this.clearContent();
      this.showClassicContentService.setShow(false);
      // this.cmsPagesService.fetchPage(path).subscribe();
    })
  );

  private pageContent$: Observable<CmsPage | null | undefined> =
    this.pagePath$.pipe(
      switchMap((path) =>
        this.cmsContentService.getPageAsJson(path).pipe(
          catchError((err) => {
            if (err.status === 404) {
              // Ignore 404 errors from CMS and fallback to v1
              return of(undefined);
            }

            console.error(err);
            return of(null);
          }),
          map((page) => {
            if (page) {
              return { path, content: page };
            }

            return page as CmsPage | null | undefined;
          })
        )
      )
    );

  ngAfterViewInit(): void {
    this.watchForContent();
  }

  ngOnDestroy(): void {
    this.showClassicContentService.setShow(false);
  }

  private watchForContent() {
    this.pageContent$
      .pipe(
        untilDestroyed(this),
        tap((page) => {
          this.metaDataService.setMetaData(page?.content?.metatags);
        })
        // Commenting this out, because it's causing the page to not render
        // but leaving it in place because it may be needed when we get Elf working again
        // distinctUntilChanged((a, b) => {
        //   console.log({ a, b });
        //   // we always want to emit if we get an undefined (i.e. 404 -> 404)
        //   // otherwise emit only if the path changed
        //   const pathsDefined = a !== undefined || b !== undefined;
        //   const pathsMatch = a?.path === b?.path;
        //   return pathsDefined && pathsMatch;
        // })
      )
      .subscribe((page) => {
        if (page === null) {
          this.contentContainer.createEmbeddedView(this.errorMessage);
          return;
        }

        if (page?.content && page.content.pageContent.length > 0) {
          this.showCmsPage();
        } else {
          this.showV1Page();
        }
      });
  }

  private clearContent() {
    this.contentContainer?.clear();
  }

  private showCmsPage() {
    this.showClassicContentService.setShow(false);
    this.contentContainer?.createComponent(CmsPageComponent);

    // TODO: This is a band-aid fix for prerender performance, we need
    // to see if there's a better way to wait for the render to complete
    setTimeout(() => {
      this.prerenderService.setReady(true);
    }, 750);
  }

  private showV1Page() {
    if (!isPlatformBrowser(this.platformId)) {
      console.log('Skipping loading classic MFE on server.');
      return;
    }

    this.showClassicContentService.setShow(true);
  }

  private show404Page() {
    this.contentContainer.createComponent(NotFoundSplashComponent);
  }
}
